// src/useKnotsInscribe.js
import { useState, useEffect, useRef } from "react";

// local copies of the helpers the hook needs (no change in behavior)
const fetchJson = async (url, opts = {}) => {
  const r = await fetch(url, opts);
  const t = await r.text();
  let j;
  try {
    j = JSON.parse(t || "{}");
  } catch {
    j = {};
  }
  if (!r.ok) {
    const e = new Error(j.error || t || `HTTP ${r.status}`);
    e.status = r.status;
    e.data = j;
    throw e;
  }
  return j;
};

const fetchJsonAllowError = async (url, opts = {}) => {
  const r = await fetch(url, opts);
  const t = await r.text();
  let j;
  try {
    j = JSON.parse(t || "{}");
  } catch {
    j = {};
  }
  return { ok: r.ok, status: r.status, data: j };
};

const b64FromBlob = async (blob) => {
  const buf = await blob.arrayBuffer();
  const bin = String.fromCharCode(...new Uint8Array(buf));
  return btoa(bin);
};

const signWithUniSat = (psbt) =>
  window.unisat.signPsbt(psbt, { autoFinalize: false });

async function signRevealParentOnly(psbtB64, address) {
  try {
    return await window.unisat.signPsbt(psbtB64, {
      autoFinalize: false,
      toSignInputs: [{ index: 0, address }],
    });
  } catch {
    return await window.unisat.signPsbt(psbtB64, {
      autoFinalize: false,
    });
  }
}

/**
 * useKnotsInscribe
 * Wraps all wallet + inscription logic so the main component is mostly UI.
 *
 * config: { ORD_API, ORCH_API, CLAIM_TTL, SHOW_RECIPIENT }
 */
export default function useKnotsInscribe(config) {
  const { ORD_API, ORCH_API, CLAIM_TTL, SHOW_RECIPIENT } = config;

  const [mode, setMode] = useState("gallery"); // 'gallery' | 'inscribe'

  /* wallet */
  const [info, setInfo] = useState(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [walletErr, setWalletErr] = useState(null);

  /* inscribe state */
  const [waitingForWallet, setWaitingForWallet] = useState(false);
  const [knotsErr, setKnotsErr] = useState(null);
  const [noStock, setNoStock] = useState(false);
  const [retrySec, setRetrySec] = useState(0);

  const [formOpen, setFormOpen] = useState(false);
  const [claimedKey, setClaimedKey] = useState(null);
  const [claimId, setClaimId] = useState(null);
  const [claimStart, setClaimStart] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  // When /complete succeeds OR the user closes the flow, we treat the reservation as "handled"
  // and avoid auto-release in the unmount effect.
  const completedRef = useRef(false);

  // ✅ once /mint-start succeeds, we DO NOT auto-release the claim on unmount/close.
  // This prevents the "double mint" window if the user closes or refreshes mid-flow.
  const mintStartedRef = useRef(false);

  // ✅ Prevent quote race: only the latest quote response may update state
  const quoteSeqRef = useRef(0);

  /* quote/broadcast */
  const [feeRate, setFeeRate] = useState(2);
  const [recipient, setRecipient] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteErr, setQuoteErr] = useState(null);
  const [actionErr, setActionErr] = useState(null);
  const [busyStep, setBusyStep] = useState("");
  const [revealTxid, setRevealTxid] = useState(null);

  async function ensureConnected() {
    if (info?.address) return true;
    if (!window.unisat) {
      setWalletErr("UniSat Wallet not found");
      return false;
    }
    setWaitingForWallet(true);
    setWalletBusy(true);
    setWalletErr(null);
    try {
      const [address] = await window.unisat.requestAccounts();
      const pubkey = await window.unisat.getPublicKey();
      setInfo({ address, pubkey });
      return true;
    } catch (e) {
      setWalletErr(e?.message?.includes("User") ? "" : e?.message || String(e));
      return false;
    } finally {
      setWalletBusy(false);
      setWaitingForWallet(false);
    }
  }

  function disconnect() {
    setInfo(null);
  }

  /* countdown */
  useEffect(() => {
    if (!claimStart) return;
    const id = setInterval(() => {
      const elapsed = Math.floor((Date.now() - claimStart) / 1000);
      const remain = CLAIM_TTL - elapsed;
      setSecondsLeft(remain > 0 ? remain : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [claimStart, CLAIM_TTL]);

  /* no-stock retry timer */
  useEffect(() => {
    if (!noStock) return;
    setRetrySec(10);
    const t = setInterval(() => setRetrySec((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [noStock]);

  /* release claim on unmount if not completed AND mint hasn't started */
  useEffect(() => {
    const asset = claimedKey,
      cid = claimId;
    return () => {
      if (asset && cid && !completedRef.current && !mintStartedRef.current) {
        fetch(`${ORCH_API}/release`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ asset_id: asset, claim_id: cid }),
        }).catch(() => {});
      }
    };
  }, [claimedKey, claimId, ORCH_API]);

  /* internal quote helper */
  async function getQuoteInternal(opts = {}) {
    const assetId = opts.assetId || claimedKey;
    const cid = opts.cid || claimId;
    if (!info || !assetId || !cid) return;

    // ✅ sequence guard (prevents stale async quotes from overwriting newer quotes)
    const mySeq = ++quoteSeqRef.current;

    setQuoting(true);
    setQuoteErr(null);
    setActionErr(null);
    try {
      const fileRes = await fetch(
        `${ORCH_API}/file/${encodeURIComponent(assetId)}?claim=${encodeURIComponent(
          cid
        )}`
      );
      if (!fileRes.ok) throw new Error(`Download failed (${fileRes.status})`);
      const blob = await fileRes.blob();
      const fileBase64 = await b64FromBlob(blob);

      let numericFeeRate = Number(feeRate);
      if (!Number.isFinite(numericFeeRate) || numericFeeRate < 2)
        numericFeeRate = 2;

      const body = {
        address: info.address,
        fileBase64,
        mime: blob.type || "application/octet-stream",
        feeRate: numericFeeRate,
      };
      if (SHOW_RECIPIENT && recipient.trim()) body.recipient = recipient.trim();

      const { ok, status, data } = await fetchJsonAllowError(`${ORD_API}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      // ✅ If another quote started after this one, ignore this response
      if (mySeq !== quoteSeqRef.current) return;

      if (ok) {
        setQuote({
          platformFee: data.platformFee ?? data.platform_fee_sats ?? 0,
          fee: data.networkFee ?? data.fee_estimate_sats ?? data.fee ?? 0,
          quoteId: data.quoteId,
          scriptHex: data.scriptHex,
          controlBlockHex: data.controlBlockHex,
          tapOutputHex: data.tapOutputHex,
          commitPsbt: data.commitPsbt,
          revealPsbt: data.revealPsbt,
          revealRaw: data.revealRaw,
        });
      } else if (status === 402) {
        const mint = data.platform_fee_sats ?? 0;
        const net = data.fee_estimate_sats ?? 0;
        const requiredTotal = data.required_total_sats ?? mint + net;
        setQuote({
          platformFee: mint,
          fee: net,
          quoteId: data.quoteId || "insufficient",
          insufficient: true,
          requiredTotal,
        });
        const req = Number(requiredTotal);
        setQuoteErr(
          Number.isFinite(req)
            ? `no available utxo > ${req.toLocaleString()}`
            : data.reason || "insufficient balance"
        );
      } else {
        setQuote(null);
        setQuoteErr(data?.error || `HTTP ${status}`);
      }
    } catch (e) {
      // Only clear quote if this is the latest quote attempt
      if (mySeq === quoteSeqRef.current) {
        setQuote(null);
        setQuoteErr(e?.message || String(e));
      }
    } finally {
      // Only clear quoting if this is the latest quote attempt
      if (mySeq === quoteSeqRef.current) setQuoting(false);
    }
  }

  /* start inscribe (reserve + quote + open form) */
  async function startInscribe() {
    setMode("inscribe");

    // ✅ always reset form UI first so no-stock state can’t “leak” old formOpen
    setFormOpen(false);

    setNoStock(false);
    setKnotsErr(null);
    setQuote(null);
    setQuoteErr(null);
    setActionErr(null);
    setRevealTxid(null);
    setClaimedKey(null);
    setClaimId(null);
    setSecondsLeft(CLAIM_TTL);
    setClaimStart(null);

    completedRef.current = false;
    mintStartedRef.current = false;

    // reset quote sequence so any in-flight quote from a previous flow is ignored
    quoteSeqRef.current += 1;

    const ok = await ensureConnected();
    if (!ok) return;

    try {
      const nxt = await fetchJson(`${ORCH_API}/next`);
      const key = nxt.key;
      const claim = nxt.claim_id;
      if (!key || !claim) throw new Error("No asset available");

      setClaimedKey(key);
      setClaimId(claim);
      setClaimStart(Date.now());

      const r = await fetch(
        `${ORCH_API}/file/${encodeURIComponent(key)}?claim=${encodeURIComponent(
          claim
        )}`
      );
      if (!r.ok) throw new Error(`Download failed (${r.status})`);

      setFormOpen(true);
      await getQuoteInternal({ assetId: key, cid: claim });
    } catch (e) {
      // ✅ “no stock” / all reserved
      if (e.status === 404) {
        setNoStock(true);

        // hard-clear anything that would cause form to still show
        setFormOpen(false);
        setClaimedKey(null);
        setClaimId(null);
        setClaimStart(null);
        setSecondsLeft(null);
        setQuote(null);
        setQuoteErr(null);
        setActionErr(null);

        completedRef.current = false;
        mintStartedRef.current = false;
      } else {
        setKnotsErr(e?.message || String(e));
      }
    }
  }

  /* explicit quote refresh for the UI button */
  async function refreshQuote() {
    if (!info || !claimedKey || !claimId) return;
    await getQuoteInternal({ assetId: claimedKey, cid: claimId });
  }

  /* sign + broadcast */
  async function handleInscribe() {
    if (!quote || !info || !claimId || !claimedKey || quoting) return;
    if (quote.insufficient) return;

    // ✅ freeze the quote so commit/reveal can't change mid-submit
    const q = quote;

    // Extra guard: don't allow double-submit
    if (busyStep === "sign" || busyStep === "broadcast") return;

    setBusyStep("sign");
    setActionErr(null);

    try {
      // ✅ promote reserved -> minting BEFORE signing/broadcasting
      try {
        await fetchJson(`${ORCH_API}/mint-start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            asset_id: claimedKey,
            claim_id: claimId,
            user: info.address,
          }),
        });

        // set this ONLY AFTER mint-start succeeds
        mintStartedRef.current = true;
      } catch (e) {
        if (e?.status === 403 && e?.data?.error === "max_per_wallet_reached") {
          const lim = Number(e.data.limit) || 5;
          setActionErr(`max ${lim} per wallet`);
          return;
        }
        setActionErr(e?.message || String(e));
        return;
      }

      const signedCommit = await signWithUniSat(q.commitPsbt);
      const signedReveal = q.revealPsbt
        ? await signRevealParentOnly(q.revealPsbt, info.address)
        : null;

      const postBody = q.revealPsbt
        ? { signedCommitPsbt: signedCommit, signedRevealPsbt: signedReveal }
        : { signedPsbt: signedCommit, revealHex: q.revealRaw };

      postBody.scriptHex = q.scriptHex;
      postBody.controlBlockHex = q.controlBlockHex;
      postBody.tapOutputHex = q.tapOutputHex;
      postBody.address = info.address;
      postBody.quoteId = q.quoteId;

      // ✅ REQUIRED by ord-api /api/broadcast/package
      postBody.asset_id = claimedKey;
      postBody.claim_id = claimId;

      // optional (but used by ord-api for logging + orchestrator_complete payload)
      postBody.user = info.address;

      // optional alias supported by ord-api (asset_id = body.get("asset_id") or body.get("key"))
      postBody.key = claimedKey;

      setBusyStep("broadcast");
      const { commitTxid, revealTxid: revealId } = await fetchJson(
        `${ORD_API}/broadcast/package`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(postBody),
        }
      );

      setRevealTxid(revealId);

      const mintFeeSats = Number(q.platformFee ?? 0);
      const networkFeeSats = Number(q.fee ?? 0);
      const totalFeeSats = mintFeeSats + networkFeeSats;

      try {
        const resp = await fetch(`${ORCH_API}/complete`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            asset_id: claimedKey,
            claim_id: claimId,
            user: info.address,
            commit_txid: commitTxid,
            reveal_txid: revealId,
            status: "done",
            platform_fee_sats: mintFeeSats,
            network_fee_sats: networkFeeSats,
            total_fee_sats: totalFeeSats,
          }),
        });

        const j = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          if (resp.status === 403 && j?.error === "max_per_wallet_reached") {
            const lim = Number(j.limit) || 5;
            throw new Error(`max ${lim} per wallet`);
          }
          throw new Error(j.error || `Orchestrator ${resp.status}`);
        }
        completedRef.current = true;
      } catch (e) {
        setActionErr(`Orchestrator record failed: ${e.message || e}`);
      }

      setClaimId(null);
      setClaimedKey(null);
      setClaimStart(null);
      setSecondsLeft(null);
    } catch (e) {
      const raw = e?.message || String(e);
      const lower = raw.toLowerCase();

      if (e?.status === 403 && e?.data?.error === "max_per_wallet_reached") {
        const lim = Number(e.data.limit) || 5;
        setActionErr(`max ${lim} per wallet`);
      } else if (lower.includes("max_per_wallet_reached")) {
        setActionErr("max 5 per wallet");
      } else if (lower.includes("stale quote")) {
        setActionErr("quote expired — press refresh");
      } else if (lower.includes("witness program hash mismatch")) {
        setActionErr("quote mismatch — press refresh quote and try again");
      } else if (lower.includes("mandatory-script-verify-flag-failed")) {
        setActionErr("broadcast rejected — press refresh quote and try again");
      } else {
        setActionErr(raw);
      }
    } finally {
      setBusyStep("");
    }
  }

  async function handleClose() {
    // ✅ If mint-start has already happened, do NOT release.
    // (Backend will expire minting after its TTL if the flow never completes.)
    if (claimId && claimedKey && !mintStartedRef.current) {
      await fetch(`${ORCH_API}/release`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ asset_id: claimedKey, claim_id: claimId }),
      }).catch(() => {});
    }

    completedRef.current = true;
    // keep future flows clean
    mintStartedRef.current = false;

    // also bump quote seq to ignore any in-flight quote response
    quoteSeqRef.current += 1;

    setFormOpen(false);
    setClaimId(null);
    setClaimedKey(null);
    setQuote(null);
    setQuoteErr(null);
    setActionErr(null);
    setRevealTxid(null);
    setSecondsLeft(null);
    setClaimStart(null);
    setMode("gallery");
  }

  async function handleInscribeAnother() {
    setRevealTxid(null);
    setFormOpen(false);
    await startInscribe();
  }

  const handleRetry = async () => {
    if (retrySec > 0) return;
    setNoStock(false);
    await startInscribe();
  };

  /* re-quote on feeRate change */
  useEffect(() => {
    if (!info || !claimedKey || !claimId) return;

    // ✅ prevent quote refresh while signing/broadcasting
    if (busyStep === "sign" || busyStep === "broadcast") return;

    const id = setTimeout(() => {
      getQuoteInternal({ assetId: claimedKey, cid: claimId });
    }, 400);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeRate, info, claimedKey, claimId, busyStep]);

  const remaining = secondsLeft == null ? CLAIM_TTL : secondsLeft;
  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const timerText = `${mins}:${secs.toString().padStart(2, "0")}`;

  const showPlaceholder = !quote || quoting;
  const submitDisabled =
    showPlaceholder ||
    busyStep === "sign" ||
    busyStep === "broadcast" ||
    (quote?.insufficient ?? false);

  return {
    mode,
    setMode,

    info,
    walletBusy,
    walletErr,
    waitingForWallet,
    knotsErr,

    noStock,
    retrySec,
    handleRetry,

    formOpen,
    timerText,

    feeRate,
    setFeeRate,
    recipient,
    setRecipient,

    quote,
    quoteErr,
    actionErr,
    busyStep,
    quoting,
    revealTxid,
    submitDisabled,

    ensureConnected,
    disconnect,
    startInscribe,
    handleInscribe,
    handleClose,
    handleInscribeAnother,
    refreshQuote,
  };
}
